import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/Dialog";
import { cn } from "@/utils/cn";
import { Loader2, Plus, Sparkles } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { applyFastNormalization, suggestFastNormalization } from "../api";
import type { FastCell, FastColumn, FastRow } from "../types";
import { FadeText } from "./Fade";

type Value = { value: string; count: number; current: string };

/** The value as read, before it was grouped under a label. */
export const originalOf = (cell: FastCell) => {
  if (cell.original) return cell.original;
  const found = /^"([\s\S]*)" → .+$/.exec(cell.derived ?? "");
  return found ? found[1] : String(cell.value);
};

const valuesOf = (rows: FastRow[], key: string): Value[] => {
  const seen = new Map<string, Value>();
  for (const row of rows) {
    const cell = row.cells[key];
    if (!cell || cell.parts.length || !String(cell.value).trim()) continue;
    const raw = originalOf(cell);
    const item = seen.get(raw) ?? { value: raw, count: 0, current: String(cell.value) };
    item.count += 1;
    seen.set(raw, item);
  }
  return [...seen.values()].sort((a, b) => b.count - a.count);
};

type Line = { x1: number; y1: number; x2: number; y2: number; changed: boolean; lit: boolean };

/** Group a column's values: each value as read (left) runs to the label it takes (right). Clicking a value shows
 * its label; clicking a label shows its values; the rest fades. With a value shown, clicking another label moves it
 * there. Approve writes the labels into the table. */
export const NormalizeDialog = ({
  datasetId,
  column,
  rows,
  onClose,
  onApplied,
}: {
  datasetId: string;
  column: FastColumn;
  rows: FastRow[];
  onClose: () => void;
  onApplied: () => void;
}) => {
  const values = useMemo(() => valuesOf(rows, column.key), [rows, column.key]);
  const [target, setTarget] = useState<Record<string, string>>(() => Object.fromEntries(values.map((v) => [v.value, v.current])));
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [extra, setExtra] = useState<string[]>([]);
  /** The value whose label is shown, and which a label click moves. */
  const [picked, setPicked] = useState<string | null>(null);
  /** The label whose values are shown. */
  const [shownLabel, setShownLabel] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState("");

  const suggest = useCallback(async () => {
    setSuggesting(true);
    setError(null);
    try {
      const found = await suggestFastNormalization(datasetId, column.key);
      setTarget((current) => ({ ...current, ...Object.fromEntries(found.values.map((v, i) => [v.value, found.groups[i]])) }));
      setReasons(Object.fromEntries(found.values.map((v, i) => [v.value, found.reasons[i] ?? ""]).filter(([, r]) => r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "The values could not be grouped.");
    } finally {
      setSuggesting(false);
    }
  }, [datasetId, column.key]);

  const grouped = values.some((v) => v.current !== v.value);
  const asked = useRef(false);
  useEffect(() => {
    if (!grouped && !asked.current && values.length > 1) {
      asked.current = true;
      void suggest();
    }
  }, [grouped, values.length, suggest]);

  const labels = useMemo(() => {
    const count = new Map<string, number>();
    for (const v of values) count.set(target[v.value], (count.get(target[v.value]) ?? 0) + v.count);
    const used = [...count.keys()].sort((a, b) => (count.get(b) ?? 0) - (count.get(a) ?? 0));
    return [...used, ...extra.filter((x) => !count.has(x))].map((label) => ({ label, count: count.get(label) ?? 0 }));
  }, [values, target, extra]);
  const order = useMemo(() => {
    const at = new Map(labels.map((l, i) => [l.label, i]));
    return [...values].sort((a, b) => (at.get(target[a.value]) ?? 0) - (at.get(target[b.value]) ?? 0) || b.count - a.count);
  }, [values, labels, target]);

  const focusLabel = picked ? target[picked] : shownLabel;
  const lit = (v: Value) => (picked ? v.value === picked : shownLabel ? target[v.value] === shownLabel : false);
  const focusing = picked !== null || shownLabel !== null;
  const board = useRef<HTMLDivElement>(null);
  const left = useRef(new Map<string, HTMLElement>());
  const right = useRef(new Map<string, HTMLElement>());
  const [lines, setLines] = useState<Line[]>([]);
  const [height, setHeight] = useState(0);
  const measure = useCallback(() => {
    const box = board.current?.getBoundingClientRect();
    if (!box) return;
    const next: Line[] = [];
    for (const v of order) {
      const a = left.current.get(v.value)?.getBoundingClientRect();
      const b = right.current.get(target[v.value])?.getBoundingClientRect();
      if (!a || !b) continue;
      next.push({
        x1: a.right - box.left,
        y1: a.top - box.top + a.height / 2,
        x2: b.left - box.left,
        y2: b.top - box.top + b.height / 2,
        changed: target[v.value] !== v.current,
        lit: picked ? v.value === picked : shownLabel ? target[v.value] === shownLabel : false,
      });
    }
    setLines(next);
    setHeight(box.height);
  }, [order, target, picked, shownLabel]);
  useLayoutEffect(measure, [measure]);
  useEffect(() => {
    if (!board.current) return;
    const observer = new ResizeObserver(measure);
    observer.observe(board.current);
    return () => observer.disconnect();
  }, [measure]);

  const route = (label: string) => {
    if (!picked) return;
    setTarget((current) => ({ ...current, [picked]: label }));
    setReasons(({ [picked]: _old, ...rest }) => rest);
    setPicked(null);
    setShownLabel(label);
  };
  const clickValue = (value: string) => {
    setShownLabel(null);
    setPicked(picked === value ? null : value);
  };
  const clickLabel = (label: string) => {
    if (picked && target[picked] !== label) return route(label);
    setPicked(null);
    setShownLabel(shownLabel === label && !picked ? null : label);
  };
  const pickedValue = values.find((v) => v.value === picked);
  const labelValues = shownLabel ? values.filter((v) => target[v.value] === shownLabel) : [];
  useEffect(() => {
    if (!shownLabel || picked) return;
    const first = order.find((v) => target[v.value] === shownLabel);
    if (first) left.current.get(first.value)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownLabel]);
  const addLabel = () => {
    const label = newLabel.trim();
    if (!label) return;
    if (!labels.some((l) => l.label === label)) setExtra((current) => [...current, label]);
    if (picked) route(label);
    setNewLabel("");
  };
  const moved = values.filter((v) => target[v.value] !== v.current).length;
  const approve = async () => {
    setSaving(true);
    setError(null);
    try {
      await applyFastNormalization(
        datasetId,
        column.key,
        values.map((v) => ({ value: v.value, label: target[v.value], reason: reasons[v.value] ?? "" })),
      );
      onApplied();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "The groups could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>Normalize {column.label}</DialogTitle>
          <DialogDescription>
            Each value as the sources write it (left) goes to one label (right). Click a value to see its label, or a label to see
            its values. To move a value, click it, then click the label it belongs to.
          </DialogDescription>
        </DialogHeader>
        <div className="font-ui flex items-center gap-3 text-xs text-zinc-500">
          <span>
            {values.length} {values.length === 1 ? "value" : "values"} → {labels.filter((l) => l.count).length} labels
            {moved > 0 && ` · ${moved} to change`}
          </span>
          <button type="button" className="ml-auto flex items-center gap-1 underline hover:text-zinc-900 disabled:opacity-50" onClick={() => void suggest()} disabled={suggesting}>
            {suggesting ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Sparkles className="h-3 w-3" aria-hidden />}
            {suggesting ? "Grouping the values" : "Suggest groups"}
          </button>
        </div>
        <div className="font-ui flex h-5 min-w-0 items-center gap-1.5 text-xs text-zinc-600" role="status">
          {pickedValue ? (
            <>
              <FadeText className="max-w-[45%] font-medium text-zinc-900" title={pickedValue.value}>
                “{pickedValue.value}”
              </FadeText>
              <span className="shrink-0">→</span>
              <FadeText className="max-w-[30%] font-medium text-zinc-900">{target[pickedValue.value]}</FadeText>
              <span className="shrink-0 text-zinc-400">· click another label to move it</span>
            </>
          ) : shownLabel ? (
            <>
              <FadeText className="max-w-[50%] font-medium text-zinc-900">{shownLabel}</FadeText>
              <span className="shrink-0 tabular-nums text-zinc-500">
                {labelValues.length
                  ? `${labelValues.length} ${labelValues.length === 1 ? "value" : "values"} · ${labelValues.reduce((n, v) => n + v.count, 0)} rows, highlighted`
                  : "no values yet"}
              </span>
            </>
          ) : (
            <span className="text-zinc-400">Click a value or a label to see where it goes.</span>
          )}
        </div>
        <div
          className="min-h-0 flex-1 overflow-y-auto"
          onClick={(e) => {
            if (!(e.target as HTMLElement).closest("button, input, form")) {
              setPicked(null);
              setShownLabel(null);
            }
          }}
        >
          <div ref={board} className="relative grid grid-cols-[minmax(0,1fr)_96px_minmax(0,0.8fr)] py-1">
            <ul className="space-y-1" aria-label="Values as read">
              {order.map((v) => (
                <li key={v.value} ref={(el) => void (el ? left.current.set(v.value, el) : left.current.delete(v.value))}>
                  <button
                    type="button"
                    onClick={() => clickValue(v.value)}
                    aria-pressed={picked === v.value}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-[2px] border px-2 py-1 text-left text-xs transition-opacity",
                      picked === v.value
                        ? "border-zinc-900 bg-zinc-900 text-white"
                        : lit(v)
                          ? "border-zinc-900 bg-zinc-100"
                          : "border-zinc-200 hover:border-zinc-400",
                      focusing && !lit(v) && "opacity-35 hover:opacity-100",
                    )}
                    title={reasons[v.value] || v.value}
                  >
                    <FadeText className="flex-1">{v.value}</FadeText>
                    <span className={cn("tabular-nums", picked === v.value ? "text-zinc-300" : "text-zinc-400")}>{v.count}</span>
                  </button>
                </li>
              ))}
            </ul>
            <svg aria-hidden className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" style={{ height }}>
              {lines.map((l, i) => (
                <path
                  key={i}
                  d={`M ${l.x1} ${l.y1} C ${(l.x1 + l.x2) / 2} ${l.y1}, ${(l.x1 + l.x2) / 2} ${l.y2}, ${l.x2} ${l.y2}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={l.lit ? 1.75 : 1}
                  strokeDasharray={l.changed ? "4 3" : undefined}
                  className={l.lit ? "text-zinc-900" : focusing ? "text-zinc-200" : l.changed ? "text-zinc-500" : "text-zinc-300"}
                />
              ))}
            </svg>
            <div aria-hidden />
            <ul className="space-y-1" aria-label="Labels">
              {labels.map(({ label, count }) => (
                <li key={label} ref={(el) => void (el ? right.current.set(label, el) : right.current.delete(label))}>
                  <button
                    type="button"
                    onClick={() => clickLabel(label)}
                    aria-pressed={focusLabel === label}
                    title={picked && target[picked] !== label ? `Move “${picked}” here` : undefined}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-[2px] border px-2 py-1 text-left text-xs font-medium transition-opacity",
                      focusLabel === label
                        ? shownLabel === label
                          ? "border-zinc-900 bg-zinc-900 text-white"
                          : "border-zinc-900 bg-zinc-100"
                        : "border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50",
                      !count && focusLabel !== label && "border-dashed text-zinc-400",
                      focusing && focusLabel !== label && !picked && "opacity-35 hover:opacity-100",
                    )}
                  >
                    <FadeText className="flex-1">{label}</FadeText>
                    <span className={cn("tabular-nums font-normal", shownLabel === label ? "text-zinc-300" : "text-zinc-400")}>{count}</span>
                  </button>
                </li>
              ))}
              <li>
                <form
                  className="flex items-center gap-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    addLabel();
                  }}
                >
                  <input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="New label"
                    aria-label="New label"
                    className="min-w-0 flex-1 rounded-[2px] border border-zinc-200 px-2 py-1 text-xs focus:border-zinc-400 focus:outline-none"
                  />
                  <button type="submit" className="rounded-[2px] border border-zinc-200 p-1 text-zinc-500 hover:text-zinc-900" aria-label="Add label">
                    <Plus className="h-3 w-3" aria-hidden />
                  </button>
                </form>
              </li>
            </ul>
          </div>
        </div>
        {error && <p className="text-xs text-red-700">{error}</p>}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void approve()} disabled={saving || suggesting || moved === 0}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Approve{moved > 0 ? ` ${moved} ${moved === 1 ? "change" : "changes"}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
